import apiClient from './client';

/** 用户中心（用户管理 / 租户成员 / 当前用户 / 用户级与全局配置）API 客户端。 */

export interface TenantMembership {
  tenant_id: string;
  role: string;
  /** 超管租户切换器等场景按 slug 匹配 */
  slug?: string;
  tenant_name?: string;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tenants?: TenantMembership[];
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
}

export interface CreateUserPayload {
  email: string;
  display_name: string;
}

/** 平台全局设置（USER scope 之外的 system scope 配置） */
export interface ResolvedConfig<T> {
  key: string;
  value: T | null;
}

// ── 当前用户与配置 ──

export async function getCurrentUser(): Promise<{ id: string; platform_role?: string; tenants?: TenantMembership[] }> {
  const res = await apiClient.get('/auth/me');
  return res.data;
}

/** 读取某 scope 下的已解析配置；未保存过返回 null */
export async function getResolvedConfig<T>(
  key: string,
  params: { user_id?: string } = {}
): Promise<T | null> {
  const res = await apiClient.get('/configs/resolved', { params: { key, ...params } });
  return ((res.data?.value as T | null) ?? null) as T | null;
}

export async function saveConfig(payload: {
  key: string;
  value: object;
  scope: 'user' | 'system';
  scope_id?: string;
  description?: string;
}): Promise<void> {
  await apiClient.put('/configs', payload);
}

// ── 用户 / 租户管理 ──

export async function listUsers(): Promise<User[]> {
  const res = await apiClient.get('/users');
  return res.data;
}

export async function listTenants(): Promise<Tenant[]> {
  const res = await apiClient.get('/tenants');
  return res.data;
}

export async function getUser(userId: string): Promise<{ tenants: TenantMembership[] }> {
  const res = await apiClient.get(`/users/${userId}`);
  return res.data;
}

export async function createUser(payload: CreateUserPayload): Promise<User> {
  const res = await apiClient.post('/users', payload);
  return res.data;
}

export async function updateUser(userId: string, payload: { is_active?: boolean }): Promise<User> {
  const res = await apiClient.patch(`/users/${userId}`, payload);
  return res.data;
}

export async function deleteUser(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}`);
}

export async function addUserToTenant(userId: string, tenantId: string, role: string): Promise<void> {
  await apiClient.post(`/users/${userId}/tenants`, { tenant_id: tenantId, role });
}

export async function removeUserFromTenant(userId: string, tenantId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}/tenants/${tenantId}`);
}
